import { Box, Button, Collapse, Typography } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Extract the visible plain text of an HTML string
export function extractVisibleText(htmlString) {
  if (htmlString === undefined || htmlString === null) return '';

  const tempDiv = document.createElement('div');

  // Parse HTML
  tempDiv.innerHTML = htmlString;

  // Remove <style>, <script>, and hidden elements
  const stylesAndScripts = tempDiv.querySelectorAll(
    'style, script, head, link, meta',
  );
  stylesAndScripts.forEach((el) => el.remove());

  // Extract visible text
  let textContent = tempDiv.textContent || tempDiv.innerText || '';
  textContent = textContent.replace(/[\u200B-\u200D\uFEFF]/g, '');
  return textContent.trim().replace(/\s+/g, ' ');
}

// Approximate the number of visual lines an HTML string renders to by
// counting explicit line breaks and closing block-level elements
export function countVisibleLines(htmlString) {
  if (!htmlString) return 0;

  const lineBreaks = htmlString.match(
    /<br[\s/>]|<\/(p|div|li|h[1-6]|tr|blockquote|pre)>/gi,
  );
  return (lineBreaks ? lineBreaks.length : 0) + 1;
}

// Extract a truncated plain text preview of an HTML string
export function extractInitialText(htmlString, charLimit = 150) {
  if (htmlString === undefined || htmlString === null) return undefined;

  const cleaned = extractVisibleText(htmlString);
  return (
    cleaned.slice(0, charLimit) + (cleaned.length > charLimit ? '...' : '')
  );
}

// Collapses long HTML content (notes, protocols, agendas) to a short
// plain text preview with an expand/collapse toggle. `children` is the
// fully rendered content and is only shown when expanded; content whose
// text fits within `charLimit` and `maxLines` renders directly without
// a toggle.
export const CollapsibleNote = ({
  HTML,
  charLimit = 150,
  maxLines = 10,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const text = extractVisibleText(HTML);
  const isCollapsible =
    text.length > charLimit ||
    countVisibleLines(HTML) > maxLines ||
    /<img[\s>]/i.test(HTML || '');

  if (!isCollapsible) return <Box className={className}>{children}</Box>;

  const preview = extractInitialText(HTML, charLimit);

  return (
    <Box className={`flex flex-col gap-1 ${className || ''}`}>
      <Button
        sx={{ padding: 0, justifyContent: 'space-between' }}
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between"
      >
        <Typography
          fontWeight={500}
          fontSize="12px"
          className="opacity-60 text-left line-clamp-2"
          sx={{ maxWidth: 'calc(100% - 24px)', overflow: 'hidden' }}
        >
          {open ? t('Show less') : preview || t('Show more')}
        </Typography>
        <ArrowDropDownIcon
          sx={{
            transform: `rotate(${open ? 180 : 0}deg)`,
            transition: 'transform 0.2s',
          }}
        />
      </Button>
      <Collapse in={open}>{children}</Collapse>
    </Box>
  );
};
